import { ChangeDetectorRef, Directive, ElementRef, OnInit, Renderer2, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { catchError, from, map, mergeMap, Observable, of, tap, toArray } from 'rxjs';
import { AlertToastService } from '../../services/alert-toast.service';
import { SmartFormContainer } from './smart-form-container';
import { IAttachedFile } from '../interfaces';

@Directive()
export abstract class DocumentUploaderComponent<TFile extends IAttachedFile>
  extends SmartFormContainer
  implements OnInit
{
  @ViewChild('uploadBlock') uploadBlock: ElementRef;
  @ViewChild('uploadInput') uploadInput: ElementRef;

  protected readonly draggingClassName = 'dragging';
  protected filesAttachedInCurrentSession: TFile[] = [];

  protected abstract readonly validators: ValidatorFn[];

  protected abstract readonly MAX_FILE_SIZE_MB: number;
  protected abstract readonly MAX_FILE_SIZE_B: number;
  protected abstract readonly GCS_MAX_FILE_AMOUNT_PER_EDIT: number;
  protected abstract readonly ALLOWED_EXTENSIONS: string[];

  protected abstract getFileData(file: TFile): TFile;
  protected abstract buildFileItem(file: File, content: string): TFile;
  protected abstract handleFiles(files: File[]): void;

  controlName = 'documentUploader';

  protected constructor(
    protected fb: UntypedFormBuilder,
    protected cd: ChangeDetectorRef,
    protected renderer: Renderer2,
    protected alertToastService: AlertToastService,
  ) {
    super(fb);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.listenDocumentUploaderChanges();
  }

  override addFormControls(): void {
    this.form.addControl(this.controlName, this.fb.array([], this.validators));
  }

  protected buildSeat(file: TFile): UntypedFormGroup {
    return this.fb.group(this.getFileData(file));
  }

  get documentUploader(): UntypedFormArray {
    return this.form?.get(this.controlName) as UntypedFormArray;
  }

  handleDragEnter(): void {
    this.renderer.addClass(this.uploadBlock.nativeElement, this.draggingClassName);
  }

  handleDragLeave(): void {
    this.renderer.removeClass(this.uploadBlock.nativeElement, this.draggingClassName);
  }

  handleFileDrop(event: DragEvent): void {
    this.renderer.removeClass(this.uploadBlock.nativeElement, this.draggingClassName);
  }

  handleFileChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files;

    if (files.length) {
      this.handleFiles(Array.from(files));
      target.value = '';
    }
  }

  displayLoadingIndicatorForFile(file: TFile): boolean {
    return !file.UUID && !file.isUploaded;
  }

  resetInputValue(): void {
    this.uploadInput.nativeElement.value = '';
  }

  protected readFilesAndAttachSequentially(files: File[]): Observable<TFile[]> {
    return from(files).pipe(
      mergeMap((file) =>
        this.readFileAsDataURL(file).pipe(
          catchError(() => {
            this.alertToastService.pushError(`Помилка при читанні файлу ${file.name}`);
            return of(null);
          }),
          map((content: string) => this.buildFileItem(file, content)),
        ),
      ),
      toArray(),
      map((attachedFiles) => attachedFiles.filter((f): f is TFile => f !== null)),
      tap((attachedFiles) => {
        attachedFiles.forEach((attachedFile) => this.documentUploader.push(this.buildSeat(attachedFile)));
        this.cd.markForCheck();
      }),
    );
  }

  protected onBatchRejected(): void {
    this.resetInputValue();
  }

  private rejectBatch(errorMessage: string): false {
    this.alertToastService.pushError(errorMessage);
    this.onBatchRejected();
    return false;
  }

  protected canAcceptBatch(files: File[]): boolean {
    if (files.length === 1 && !this.isValidSingleFile(files[0])) {
      return false;
    }

    return this.isValidBatchLimits(files);
  }

  private isValidBatchLimits(files: File[]): boolean {
    const totalCount = this.filesAttachedInCurrentSession.length + files.length;
    if (totalCount > this.GCS_MAX_FILE_AMOUNT_PER_EDIT) {
      return this.rejectBatch(`Максимальна кількість файлів — ${this.GCS_MAX_FILE_AMOUNT_PER_EDIT}`);
    }

    const existingSize = this.filesAttachedInCurrentSession.reduce((acc, f) => acc + (f.size ?? 0), 0);
    const incomingSize = files.reduce((acc, f) => acc + f.size, 0);
    const maxTotalSize = this.GCS_MAX_FILE_AMOUNT_PER_EDIT * this.MAX_FILE_SIZE_B;

    if (existingSize + incomingSize > maxTotalSize) {
      return this.rejectBatch(`Загальний розмір файлів перевищує ${this.MAX_FILE_SIZE_MB * this.GCS_MAX_FILE_AMOUNT_PER_EDIT} Мб`);
    }

    return true;
  }

  private isValidSingleFile(file: File): boolean {
    if (file.size === 0) {
      return this.rejectBatch('Отриманий файл пустий');
    }

    if (file.size > this.MAX_FILE_SIZE_B) {
      return this.rejectBatch(`Розмір файлу перевищує ${this.MAX_FILE_SIZE_MB} Мб`);
    }

    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!this.ALLOWED_EXTENSIONS.includes(ext)) {
      return this.rejectBatch('Недопустиме розширення файлу');
    }

    return true;
  }

  private readFileAsDataURL(file: File): Observable<string> {
    return new Observable<string>((subscriber) => {
      const reader = new FileReader();
      reader.addEventListener(
        'load',
        () => {
          if (reader.result) {
            subscriber.next(this.getFileContentAsString(reader));
            subscriber.complete();
          } else {
            subscriber.error(new Error('Empty file content'));
          }
        },
        { once: true },
      );
      reader.addEventListener('error', () => subscriber.error(reader.error), { once: true });
      reader.readAsDataURL(file);
      return () => reader.abort();
    });
  }

  private getFileContentAsString(reader: FileReader): string {
    const result = reader.result as string;
    return result.substring(result.lastIndexOf(',') + 1);
  }

  protected listenDocumentUploaderChanges(): void {
    this.subscriptions = this.documentUploader.valueChanges.subscribe(() => this.cd.markForCheck());
  }
}
